
import React, { useState, useEffect, useRef } from "react";
import Navbar from "../widgets/Navbar";
import Footer from "../widgets/Footer";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useMakeCryptoPaymentMutation, useLazyGetPaymentStatusQuery } from "../../redux/paymentApi";

const CryptadiumPayment = () => {
  const { t } = useTranslation();
  const [makeCryptoPayment, { isLoading: apiLoading }] =
    useMakeCryptoPaymentMutation();
  const { userData } = useSelector((state) => state.user);
  const [formData, setFormData] = useState({ amount: "", currency: "USDT" });
  const [formErrors, setFormErrors] = useState({ amount: "" });
  const [paymentStarted, setPaymentStarted] = useState(false);
  const [billingId, setBillingId] = useState(null);
  const [countdown, setCountdown] = useState(300); 
  const [triggerGetPaymentStatus] = useLazyGetPaymentStatusQuery();


  const intervalRef = useRef();

  const validateAmount = (value) => {
    if (!value) return "Amount is required";
    const num = parseFloat(value);
    if (isNaN(num)) return "Amount must be a valid number";
    if (num <= 0) return "Amount must be greater than 0";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedForm = { ...formData, [name]: value };
    setFormData(updatedForm);
    if (name === "amount") {
      const errorMsg = validateAmount(value);
      setFormErrors({ ...formErrors, amount: errorMsg });
    }
  };
  const handlePayment = async () => {
    const amountError = validateAmount(formData.amount);
    if (amountError) {
      setFormErrors({ ...formErrors, amount: amountError });
      toast.error(amountError);
      return;
    }
  
    try {
      const response = await makeCryptoPayment({
        amount: formData.amount,
        currency: formData.currency,
        contact: userData?.email,
        user_id: userData?.user_name,
      }).unwrap();
  
      if (response?.status) {
        const billingId = response?.billing_id;
        if (!billingId) {
          toast.error("Billing ID missing from response");
          return;
        }
  
        setBillingId(billingId); 
        setPaymentStarted(true); 
        window.open(response?.payment_url, "_blank"); 
        startPolling(billingId);
      } else {
        const message = response?.message || "Payment request failed.";
        toast.error(message);
      }
    } catch (error) {
      console.error("API error:", error);
      const message = error?.data?.message || "Payment request failed.";
      toast.error(message);
    }
  };
  const startPolling = (billingId) => {
    intervalRef.current = setInterval(async () => {
      try {
        const { status } = await triggerGetPaymentStatus(billingId).unwrap();
        if (status === "success") {
          clearInterval(intervalRef.current);
          setPaymentStarted(false); 
          window.location.href = "/payment-success";
        } else if (status === "failed" || status === "cancelled") {
          clearInterval(intervalRef.current);
          setPaymentStarted(false); 
          window.location.href = "/payment-cancel";
        }
      } catch (err) {
        console.error("Polling error", err);
      }
    }, 5000);
  };

  useEffect(() => {
    if (!paymentStarted) return;
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          clearInterval(intervalRef.current);
          toast.error("Payment timeout.");
          window.location.href = "/payment-cancel";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [paymentStarted]);

  // Prevent navigation
  // useEffect(() => {
  //   const warnOnLeave = (e) => {
  //     e.preventDefault();
  //     e.returnValue = "";
  //   };

  //   const blockBack = () => {
  //     window.history.pushState(null, null, window.location.href);
  //   };

  //   if (paymentStarted) {
  //     window.addEventListener("popstate", blockBack);
  //     blockBack();
  //   }

  //   return () => {
  //     window.removeEventListener("popstate", blockBack);
  //   };
  // }, [paymentStarted]);

  const isSubmitDisabled = !formData.amount || !!formErrors.amount;

  const formatTime = (sec) => {
    const m = String(Math.floor(sec / 60)).padStart(2, "0");
    const s = String(sec % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  return (
    <>
      <Navbar />
      <section className="d-flex align-items-center justify-content-center min-vh-100 mt-5 pt-5">
        <div className="container">
          <div className="row">
            <div className="col-md-6 m-auto">
              <div className="p-3 p-md-4 bg2 br20 mb-4">
                <h3 className="text-center">{t("Crypto Payment")}</h3>

                {paymentStarted ? (
                  <div className="text-center mt-4">
                    <p>{t("Waiting for payment confirmation...")}</p>
                    <h2>{formatTime(countdown)}</h2>
                    <p>{t("Do not close or refresh this page")}</p>
                  </div>
                ) : (
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="form-group">
                      <label>
                        {t("Amount")} <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t("Enter Amount")}
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                      />
                      <span className="text-danger">
                        {t(formErrors.amount)}
                      </span>
                    </div>

                    <div className="form-group">
                      <label>{t("Currency")}</label>
                      <select
                        className="form-control"
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                      >
                        <option value="USDT">Tether (USDT)</option>
                        <option value="USDC">USDCoin (USDC)</option>
                        <option value="EUR">Ethereum (ETH)</option>
                        <option value="BTC">Bitcoin (BTC)</option>
                        <option value="LTC">Litecoin (LTC)</option>
                        <option value="DOGE">Dogecoin (DOGE)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>
                        {t("Username")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData?.user_name}
                        name="user_name"
                        disabled
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        {t("Email or Mobile")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={userData?.email}
                        name="contact"
                        disabled
                      />
                    </div>

                    <button
                      className="btn w100 mt-4"
                      onClick={handlePayment}
                      disabled={isSubmitDisabled || apiLoading}
                    >
                      {apiLoading
                        ? t("Processing...")
                        : t("Pay with Cryptadium")}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
};

export default CryptadiumPayment;
